/**
 * Usage Meters Component - Dashboard view of quotas
 *
 * - Shows daily comparisons and saved comparisons meters side-by-side
 * - Color-coded progress bars (green 0-59%, yellow 60-79%, amber 80-99%, red 100%)
 * - Reset timer for daily quota
 * - "View Results" button linking to /results page
 * - Visible on both desktop and mobile (stacks on mobile)
 */

import React from 'react';
import { ClockIcon, DocumentDuplicateIcon, ArrowPathIcon, CpuChipIcon } from '@heroicons/react/24/outline';
import type { QuotaInfo, SavedComparisonsQuota } from '../../services/entitlement';
import { Tooltip } from '../shared/Tooltip';

interface TokenQuota {
  used: number;
  limit: number;
  remaining: number;
  percentage: number;
  allowed: boolean;
}

interface UsageMetersProps {
  dailyQuota: QuotaInfo;
  savedQuota?: SavedComparisonsQuota;
  tokenQuota?: TokenQuota;
  planName: string;
  userTimezone?: string;
  paywallEnabled: boolean;
}

export const UsageMeters: React.FC<UsageMetersProps> = ({
  dailyQuota,
  savedQuota,
  tokenQuota,
  planName,
  userTimezone = 'UTC',
  paywallEnabled,
}) => {

  if (!paywallEnabled) return null;

  // Log timezone for debugging
  console.log('[UsageMeters] Rendering with timezone:', {
    userTimezone,
    type: typeof userTimezone
  });

  // Calculate time until daily reset (midnight in user's timezone)
  const getTimeUntilReset = () => {
    const now = new Date();

    // Get current time in user's timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: userTimezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });

    const parts = formatter.formatToParts(now);
    const getPartValue = (type: string) => parts.find(p => p.type === type)?.value || '0';

    const hour = parseInt(getPartValue('hour'));
    const minute = parseInt(getPartValue('minute'));
    const second = parseInt(getPartValue('second'));

    // Calculate seconds until midnight in user's timezone
    const secondsUntilMidnight = (24 - hour - 1) * 3600 + (60 - minute - 1) * 60 + (60 - second);

    const hours = Math.floor(secondsUntilMidnight / 3600);
    const minutes = Math.floor((secondsUntilMidnight % 3600) / 60);

    return `${hours}h ${minutes}m`;
  };

  // Get timezone abbreviation for display
  const getTimezoneAbbr = () => {
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: userTimezone,
        timeZoneName: 'short'
      });
      const parts = formatter.formatToParts(new Date());
      const tzPart = parts.find(part => part.type === 'timeZoneName');
      const abbr = tzPart?.value || userTimezone;
      console.log('[UsageMeters] Timezone abbreviation:', {
        input: userTimezone,
        output: abbr,
        partsCount: parts.length
      });
      return abbr;
    } catch (error) {
      console.error('[UsageMeters] Error getting timezone abbr:', error);
      return userTimezone;
    }
  };

  // Get color classes based on percentage
  const getProgressColor = (percentage: number): string => {
    if (percentage >= 100) return 'bg-red-500';
    if (percentage >= 80) return 'bg-amber-500';
    if (percentage >= 60) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getTextColor = (percentage: number): string => {
    if (percentage >= 100) return 'text-red-400';
    if (percentage >= 80) return 'text-amber-400';
    if (percentage >= 60) return 'text-yellow-400';
    return 'text-green-400';
  };

  const dailyPercentage = dailyQuota.limit > 0
    ? Math.round((dailyQuota.used / dailyQuota.limit) * 100)
    : 0;

  const savedPercentage = savedQuota?.percentage || 0;

  return (
    <div className="space-y-6">
      {/* Usage Meters Stack */}
      <div className="space-y-4 sm:space-y-6">
        {/* Daily Prompts Meter */}
        <div className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-5 md:p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                <ArrowPathIcon className="w-6 h-6 text-cyan-400" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-white">Daily Prompts</h3>
                <p className="text-xs text-slate-400 mt-0.5">Resets daily at midnight {getTimezoneAbbr()}</p>
              </div>
            </div>
          </div>

          {/* Count */}
          <div className="mb-3">
            <div className="flex items-baseline gap-2">
              <span className={`text-2xl font-bold ${getTextColor(dailyPercentage)}`}>
                {dailyQuota.used}
              </span>
              <span className="text-slate-400 text-sm">
                of {dailyQuota.limit} used
              </span>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-slate-700/50 rounded-full h-3 mb-3">
            <div
              className={`h-3 rounded-full transition-all duration-300 ${getProgressColor(dailyPercentage)}`}
              style={{ width: `${Math.min(dailyPercentage, 100)}%` }}
            />
          </div>

          {/* Stats Row */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-400">
              {dailyQuota.remaining} remaining
            </span>
            <div className="flex items-center gap-1 text-slate-400">
              <ClockIcon className="w-4 h-4" />
              <span>{getTimeUntilReset()}</span>
            </div>
          </div>

          {/* Warning message and upgrade CTA at 80%+ */}
          {dailyPercentage >= 80 && (
            <div className="mt-3 pt-3 border-t border-slate-700/50">
              <p className={`text-xs mb-1.5 ${getTextColor(dailyPercentage)}`}>
                {dailyPercentage >= 100
                  ? `Daily limit reached. Resets at midnight ${getTimezoneAbbr()}.`
                  : 'Approaching your daily limit.'}
              </p>
              <a
                href="/billing/upgrade"
                className="inline-flex items-center text-xs font-medium text-cyan-400 hover:text-cyan-300 transition-colors"
              >
                Upgrade to increase daily limit →
              </a>
            </div>
          )}
        </div>

        {/* Total Comparisons Meter */}
        {savedQuota && savedQuota.limit !== -1 && (
          <div className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-5 md:p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                  <DocumentDuplicateIcon className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-white">Total Comparisons</h3>
                  <p className="text-xs text-slate-400 mt-0.5">Total storage used</p>
                </div>
              </div>
            </div>

            {/* Count */}
            <div className="mb-3">
              <div className="flex items-baseline gap-2">
                <span className={`text-2xl font-bold ${getTextColor(savedPercentage)}`}>
                  {savedQuota.count}
                </span>
                <span className="text-slate-400 text-sm">
                  of {savedQuota.limit} used
                </span>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-slate-700/50 rounded-full h-3 mb-3">
              <div
                className={`h-3 rounded-full transition-all duration-300 ${getProgressColor(savedPercentage)}`}
                style={{ width: `${Math.min(savedPercentage, 100)}%` }}
              />
            </div>

            {/* Stats Row */}
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-400">
                {savedQuota.remaining} slots remaining
              </span>
              <span className="text-slate-400 whitespace-nowrap">
                {savedPercentage}% used
              </span>
            </div>

            {/* Warning message and upgrade CTA at 80%+ */}
            {savedPercentage >= 80 && (
              <div className="mt-3 pt-3 border-t border-slate-700/50">
                <p className={`text-xs mb-1.5 ${getTextColor(savedPercentage)}`}>
                  {savedPercentage >= 100
                    ? 'Storage limit reached.'
                    : 'Running low on storage space.'}
                </p>
                <a
                  href="/billing/upgrade"
                  className="inline-flex items-center text-xs font-medium text-cyan-400 hover:text-cyan-300 transition-colors"
                >
                  Upgrade to increase your limit →
                </a>
              </div>
            )}
          </div>
        )}

        {/* Unlimited Total Comparisons Message */}
        {savedQuota && savedQuota.limit === -1 && (
          <div className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-5 md:p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                  <DocumentDuplicateIcon className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-white">Total Comparisons</h3>
                  <p className="text-xs text-slate-400 mt-0.5">Total storage used</p>
                </div>
              </div>
            </div>

            <div className="mb-3">
              <span className="text-2xl font-bold text-green-400">
                {savedQuota.count}
              </span>
              <span className="text-slate-400 text-sm ml-2">saved</span>
            </div>

            <div className="text-sm text-green-400">
              ✨ Unlimited storage on {planName}
            </div>
          </div>
        )}

        {/* Monthly Token Usage Meter */}
        {tokenQuota && (
          <div className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-5 md:p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <CpuChipIcon className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-semibold text-white">Token Usage</h3>
                    <Tooltip content="Tokens are used when you score responses or run consensus analysis on comparisons." />
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5">Resets monthly on 1st</p>
                </div>
              </div>
            </div>

            {/* Count - percentage only */}
            <div className="mb-3">
              <div className="flex items-baseline gap-2">
                <span className={`text-2xl font-bold ${getTextColor(tokenQuota.percentage)}`}>
                  {tokenQuota.percentage.toFixed(0)}%
                </span>
                <span className="text-slate-400 text-sm">
                  of monthly tokens used
                </span>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-slate-700/50 rounded-full h-3 mb-3">
              <div
                className={`h-3 rounded-full transition-all duration-300 ${getProgressColor(tokenQuota.percentage)}`}
                style={{ width: `${Math.min(tokenQuota.percentage, 100)}%` }}
              />
            </div>

            {/* Warning message and upgrade CTA at 80%+ */}
            {tokenQuota.percentage >= 80 && (
              <div className="mt-3 pt-3 border-t border-slate-700/50">
                <p className={`text-xs mb-1.5 ${getTextColor(tokenQuota.percentage)}`}>
                  {tokenQuota.percentage >= 100
                    ? 'Monthly token limit reached.'
                    : 'Approaching your monthly token limit.'}
                </p>
                <a
                  href="/billing/upgrade"
                  className="inline-flex items-center text-xs font-medium text-cyan-400 hover:text-cyan-300 transition-colors"
                >
                  Upgrade for more tokens →
                </a>
              </div>
            )}
          </div>
        )}
      </div>

    </div>
  );
};
